/**
 * Content Manager - Content Service
 *
 * Service layer for content CRUD operations using AWS API Gateway.
 */

import { apiClient, isAWSConfigured } from '../lib/awsConfig';
import { logger } from '../lib/logger';
import type {
  ContentSeed,
  ContentOutput,
  ContentGroup,
  ContentNote,
  ContentEvent,
  ContentStatus,
  ApiResponse,
} from '../types';

// =============================================================================
// API ENDPOINTS
// =============================================================================

const ENDPOINTS = {
  seeds: '/seeds',
  outputs: '/outputs',
  groups: '/groups',
  notes: '/notes',
  events: '/events',
} as const;

// =============================================================================
// SEEDS SERVICE
// =============================================================================

export const seedsService = {
  /**
   * Fetch all seeds with optional outputs
   */
  async fetchAll(includeOutputs = true): Promise<ApiResponse<ContentSeed[]>> {
    logger.debug('content', 'Fetching all seeds', { includeOutputs });

    if (!isAWSConfigured()) {
      logger.warn('content', 'Failed to fetch seeds: AWS not configured');
      return { data: [], error: null };
    }

    const endpoint = includeOutputs
      ? `${ENDPOINTS.seeds}?include=outputs`
      : ENDPOINTS.seeds;

    const result = await apiClient.get<ContentSeed[]>(endpoint);
    if (result.error) {
      logger.error('content', 'Failed to fetch seeds', result.error);
    } else {
      logger.info('content', 'Seeds fetched successfully', {
        count: result.data?.length ?? 0,
      });
    }
    return result;
  },

  /**
   * Fetch a single seed by ID
   */
  async fetchById(id: string): Promise<ApiResponse<ContentSeed>> {
    logger.debug('content', 'Fetching seed by ID', { id });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to fetch seed: AWS not configured', { id });
      return { data: null, error: new Error('AWS not configured') };
    }

    const result = await apiClient.get<ContentSeed>(`${ENDPOINTS.seeds}/${id}?include=outputs,notes`);
    if (result.error) {
      logger.error('content', 'Failed to fetch seed', { id, error: result.error.message });
    } else {
      logger.debug('content', 'Seed fetched successfully', { id });
    }
    return result;
  },

  /**
   * Create a new seed
   */
  async create(seed: Partial<ContentSeed>): Promise<ApiResponse<ContentSeed>> {
    logger.info('content', 'Creating new seed', { title: seed.title });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to create seed: AWS not configured', {
        title: seed.title,
        reason: 'AWS services are not configured. Please set up your environment variables.',
      });
      return { data: null, error: new Error('AWS not configured') };
    }

    const payload = {
      title: seed.title || 'Untitled Seed',
      description: seed.description ?? null,
      status: seed.status || 'Idea',
      priority: seed.priority || 'Medium',
      silo: seed.silo ?? null,
      targetDate: seed.targetDate ?? null,
      tags: seed.tags || [],
      groupId: seed.groupId ?? null,
      order: seed.order || 0,
    };

    const result = await apiClient.post<ContentSeed>(ENDPOINTS.seeds, payload);
    if (result.error) {
      logger.error('content', 'Failed to create seed', {
        title: seed.title,
        error: result.error.message,
      });
    } else {
      logger.info('content', 'Seed created successfully', {
        id: result.data?.id,
        title: result.data?.title,
      });
    }
    return result;
  },

  /**
   * Update an existing seed
   */
  async update(id: string, updates: Partial<ContentSeed>): Promise<ApiResponse<ContentSeed>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.patch<ContentSeed>(`${ENDPOINTS.seeds}/${id}`, updates);
  },

  /**
   * Delete a seed (cascades to outputs)
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.delete<void>(`${ENDPOINTS.seeds}/${id}`);
  },

  /**
   * Update seed status
   */
  async updateStatus(id: string, status: ContentStatus): Promise<ApiResponse<ContentSeed>> {
    return this.update(id, { status });
  },

  /**
   * Bulk update seed order
   */
  async reorder(seedOrders: { id: string; order: number }[]): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.post<void>(`${ENDPOINTS.seeds}/reorder`, { orders: seedOrders });
  },

  /**
   * Bulk update status for multiple seeds
   */
  async bulkUpdateStatus(ids: string[], status: ContentStatus): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.post<void>(`${ENDPOINTS.seeds}/bulk-status`, { ids, status });
  },

  /**
   * Bulk delete seeds
   */
  async bulkDelete(ids: string[]): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.post<void>(`${ENDPOINTS.seeds}/bulk-delete`, { ids });
  },
};

// =============================================================================
// OUTPUTS SERVICE
// =============================================================================

export const outputsService = {
  /**
   * Fetch all outputs
   */
  async fetchAll(): Promise<ApiResponse<ContentOutput[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    return apiClient.get<ContentOutput[]>(ENDPOINTS.outputs);
  },

  /**
   * Fetch outputs for a specific seed
   */
  async fetchBySeedId(seedId: string): Promise<ApiResponse<ContentOutput[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    return apiClient.get<ContentOutput[]>(`${ENDPOINTS.outputs}?seedId=${seedId}`);
  },

  /**
   * Fetch a single output by ID
   */
  async fetchById(id: string): Promise<ApiResponse<ContentOutput>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.get<ContentOutput>(`${ENDPOINTS.outputs}/${id}?include=notes`);
  },

  /**
   * Create a new output
   */
  async create(output: Partial<ContentOutput>): Promise<ApiResponse<ContentOutput>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    if (!output.seedId) {
      return { data: null, error: new Error('seedId is required to create an output') };
    }

    return apiClient.post<ContentOutput>(ENDPOINTS.outputs, {
      seedId: output.seedId,
      title: output.title || 'Untitled Output',
      contentType: output.contentType || 'Article',
      platforms: output.platforms || [],
      status: output.status || 'Idea',
      author: output.author ?? null,
      assignedTo: output.assignedTo ?? null,
      dueDate: output.dueDate ?? null,
      publishDate: output.publishDate ?? null,
      content: output.content ?? null,
      excerpt: output.excerpt ?? null,
      focusKeyword: output.focusKeyword ?? null,
      seoTitle: output.seoTitle ?? null,
      metaDescription: output.metaDescription ?? null,
      socialCopy: output.socialCopy ?? null,
      hashtags: output.hashtags ?? null,
      featuredImage: output.featuredImage ?? null,
      assets: output.assets || [],
      url: output.url ?? null,
      metrics: output.metrics || { views: 0, engagement: 0, shares: 0, clicks: 0 },
      order: output.order ?? 0,
    });
  },

  /**
   * Update an existing output
   */
  async update(id: string, updates: Partial<ContentOutput>): Promise<ApiResponse<ContentOutput>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    // Sanitize the updates to match backend validation schema
    // Remove fields that aren't part of UpdateOutputSchema
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, seedId: _seedId, createdAt: _createdAt, updatedAt: _updatedAt, notes: _notes, seed: _seed, order: _order, ...allowedFields } = updates as any;

    // Convert empty strings to null for URL fields (backend expects valid URL or null)
    const sanitizedUpdates: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(allowedFields)) {
      if (value === undefined) continue; // Skip undefined fields

      // Handle URL fields - convert empty strings to null
      if ((key === 'featuredImage' || key === 'url') && value === '') {
        sanitizedUpdates[key] = null;
      }
      // Handle assets array - filter out empty strings
      else if (key === 'assets' && Array.isArray(value)) {
        const validAssets = value.filter((url: string) => url && url.trim() !== '');
        sanitizedUpdates[key] = validAssets.length > 0 ? validAssets : [];
      }
      // Handle other optional string fields - convert empty strings to null
      else if (typeof value === 'string' && value === '' &&
               ['author', 'assignedTo', 'dueDate', 'publishDate', 'content', 'excerpt',
                'focusKeyword', 'seoTitle', 'metaDescription', 'socialCopy', 'hashtags'].includes(key)) {
        sanitizedUpdates[key] = null;
      }
      else {
        sanitizedUpdates[key] = value;
      }
    }

    return apiClient.patch<ContentOutput>(`${ENDPOINTS.outputs}/${id}`, sanitizedUpdates);
  },

  /**
   * Delete an output
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.delete<void>(`${ENDPOINTS.outputs}/${id}`);
  },

  /**
   * Update output status
   */
  async updateStatus(id: string, status: ContentStatus): Promise<ApiResponse<ContentOutput>> {
    return this.update(id, { status });
  },

  /**
   * Bulk update status for multiple outputs
   */
  async bulkUpdateStatus(ids: string[], status: ContentStatus): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.post<void>(`${ENDPOINTS.outputs}/bulk-status`, { ids, status });
  },

  /**
   * Bulk delete outputs
   */
  async bulkDelete(ids: string[]): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.post<void>(`${ENDPOINTS.outputs}/bulk-delete`, { ids });
  },

  /**
   * Reorder outputs within a seed
   */
  async reorder(outputOrders: { id: string; order: number }[]): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.post<void>(`${ENDPOINTS.outputs}/reorder`, { orders: outputOrders });
  },
};

// =============================================================================
// GROUPS SERVICE
// =============================================================================

export const groupsService = {
  /**
   * Fetch all content groups
   */
  async fetchAll(): Promise<ApiResponse<ContentGroup[]>> {
    logger.debug('content', 'Fetching all groups');

    if (!isAWSConfigured()) {
      logger.warn('content', 'Failed to fetch groups: AWS not configured');
      return { data: [], error: null };
    }

    const result = await apiClient.get<ContentGroup[]>(ENDPOINTS.groups);
    if (result.error) {
      logger.error('content', 'Failed to fetch groups', result.error);
    } else {
      logger.info('content', 'Groups fetched successfully', {
        count: result.data?.length ?? 0,
      });
    }
    return result;
  },

  /**
   * Create a new group
   */
  async create(group: Partial<ContentGroup>): Promise<ApiResponse<ContentGroup>> {
    logger.info('content', 'Creating new group', {
      title: group.title || 'Untitled Group',
    });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to create group: AWS not configured', {
        title: group.title,
        reason: 'AWS services are not configured. Please set up your environment variables.',
      });
      return { data: null, error: new Error('AWS not configured') };
    }

    const payload = {
      title: group.title || 'Untitled Group',
      backgroundColour: group.backgroundColour || '#3b82f6',
      textColour: group.textColour || '#ffffff',
      order: group.order || 0,
      isCollapsed: group.isCollapsed || false,
    };

    logger.debug('content', 'Group creation payload', payload);

    const result = await apiClient.post<ContentGroup>(ENDPOINTS.groups, payload);
    if (result.error) {
      logger.error('content', 'Failed to create group', {
        title: group.title,
        error: result.error.message,
      });
    } else {
      logger.info('content', 'Group created successfully', {
        id: result.data?.id,
        title: result.data?.title,
      });
    }
    return result;
  },

  /**
   * Update a group
   */
  async update(id: string, updates: Partial<ContentGroup>): Promise<ApiResponse<ContentGroup>> {
    logger.info('content', 'Updating group', { id, updates: Object.keys(updates) });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to update group: AWS not configured', { id });
      return { data: null, error: new Error('AWS not configured') };
    }

    const result = await apiClient.patch<ContentGroup>(`${ENDPOINTS.groups}/${id}`, updates);
    if (result.error) {
      logger.error('content', 'Failed to update group', { id, error: result.error.message });
    } else {
      logger.info('content', 'Group updated successfully', { id });
    }
    return result;
  },

  /**
   * Delete a group
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    logger.info('content', 'Deleting group', { id });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to delete group: AWS not configured', { id });
      return { data: null, error: new Error('AWS not configured') };
    }

    const result = await apiClient.delete<void>(`${ENDPOINTS.groups}/${id}`);
    if (result.error) {
      logger.error('content', 'Failed to delete group', { id, error: result.error.message });
    } else {
      logger.info('content', 'Group deleted successfully', { id });
    }
    return result;
  },

  /**
   * Reorder groups
   */
  async reorder(groupOrders: { id: string; order: number }[]): Promise<ApiResponse<void>> {
    logger.debug('content', 'Reordering groups', { count: groupOrders.length });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to reorder groups: AWS not configured');
      return { data: null, error: new Error('AWS not configured') };
    }

    const result = await apiClient.post<void>(`${ENDPOINTS.groups}/reorder`, { orders: groupOrders });
    if (result.error) {
      logger.error('content', 'Failed to reorder groups', { error: result.error.message });
    } else {
      logger.info('content', 'Groups reordered successfully');
    }
    return result;
  },
};

// =============================================================================
// NOTES SERVICE
// =============================================================================

export const notesService = {
  /**
   * Fetch all notes
   */
  async fetchAll(): Promise<ApiResponse<ContentNote[]>> {
    console.log('[notesService] fetchAll called, AWS configured:', isAWSConfigured());
    if (!isAWSConfigured()) {
      console.log('[notesService] AWS not configured, returning empty array');
      return { data: [], error: null };
    }

    const result = await apiClient.get<ContentNote[]>(ENDPOINTS.notes);
    console.log('[notesService] fetchAll result:', { data: result.data, error: result.error, count: result.data?.length });
    return result;
  },

  /**
   * Fetch notes for a seed
   */
  async fetchBySeedId(seedId: string): Promise<ApiResponse<ContentNote[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    return apiClient.get<ContentNote[]>(`${ENDPOINTS.notes}?seedId=${seedId}`);
  },

  /**
   * Fetch notes for an output
   */
  async fetchByOutputId(outputId: string): Promise<ApiResponse<ContentNote[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    return apiClient.get<ContentNote[]>(`${ENDPOINTS.notes}?outputId=${outputId}`);
  },

  /**
   * Create a note
   */
  async create(note: Partial<ContentNote>): Promise<ApiResponse<ContentNote>> {
    console.log('[notesService] create called with:', note);
    if (!isAWSConfigured()) {
      console.log('[notesService] AWS not configured, cannot create note');
      return { data: null, error: new Error('AWS not configured') };
    }

    const payload = {
      seedId: note.seedId ?? null,
      outputId: note.outputId ?? null,
      text: note.text || '',
      isActionable: note.isActionable || false,
      isComplete: note.isComplete || false,
      mentions: note.mentions || [],
      parentNoteId: note.parentNoteId ?? null,
    };
    console.log('[notesService] Creating note with payload:', payload);
    const result = await apiClient.post<ContentNote>(ENDPOINTS.notes, payload);
    console.log('[notesService] create result:', { data: result.data, error: result.error });
    return result;
  },

  /**
   * Update a note
   */
  async update(id: string, updates: Partial<ContentNote>): Promise<ApiResponse<ContentNote>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.patch<ContentNote>(`${ENDPOINTS.notes}/${id}`, updates);
  },

  /**
   * Delete a note
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    return apiClient.delete<void>(`${ENDPOINTS.notes}/${id}`);
  },

  /**
   * Toggle note completion
   */
  async toggleComplete(id: string, isComplete: boolean): Promise<ApiResponse<ContentNote>> {
    return this.update(id, { isComplete });
  },
};

// =============================================================================
// EVENTS SERVICE
// =============================================================================

export const eventsService = {
  /**
   * Fetch all events
   */
  async fetchAll(): Promise<ApiResponse<ContentEvent[]>> {
    logger.debug('content', 'Fetching all events');

    if (!isAWSConfigured()) {
      logger.warn('content', 'Failed to fetch events: AWS not configured');
      return { data: [], error: null };
    }

    const result = await apiClient.get<ContentEvent[]>(ENDPOINTS.events);
    if (result.error) {
      logger.error('content', 'Failed to fetch events', result.error);
    } else {
      logger.info('content', 'Events fetched successfully', {
        count: result.data?.length ?? 0,
      });
    }
    return result;
  },

  /**
   * Fetch a single event by ID
   */
  async fetchById(id: string): Promise<ApiResponse<ContentEvent>> {
    logger.debug('content', 'Fetching event by ID', { id });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to fetch event: AWS not configured', { id });
      return { data: null, error: new Error('AWS not configured') };
    }

    const result = await apiClient.get<ContentEvent>(`${ENDPOINTS.events}/${id}`);
    if (result.error) {
      logger.error('content', 'Failed to fetch event', { id, error: result.error.message });
    } else {
      logger.debug('content', 'Event fetched successfully', { id });
    }
    return result;
  },

  /**
   * Create a new event
   */
  async create(event: Partial<ContentEvent>): Promise<ApiResponse<ContentEvent>> {
    logger.info('content', 'Creating new event', { title: event.title });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to create event: AWS not configured', {
        title: event.title,
        reason: 'AWS services are not configured. Please set up your environment variables.',
      });
      return { data: null, error: new Error('AWS not configured') };
    }

    const payload = {
      activityType: event.activityType || 'Event',
      title: event.title || 'Untitled Event',
      location: event.location ?? null,
      startDate: event.startDate,
      endDate: event.endDate ?? null,
      notes: event.notes ?? null,
      url: event.url ?? null,
      awardsCategoryUrl: event.awardsCategoryUrl ?? null,
      entryDeadline: event.entryDeadline ?? null,
      order: event.order || 0,
    };

    const result = await apiClient.post<ContentEvent>(ENDPOINTS.events, payload);
    if (result.error) {
      logger.error('content', 'Failed to create event', {
        title: event.title,
        error: result.error.message,
      });
    } else {
      logger.info('content', 'Event created successfully', {
        id: result.data?.id,
        title: result.data?.title,
      });
    }
    return result;
  },

  /**
   * Update an existing event
   */
  async update(id: string, updates: Partial<ContentEvent>): Promise<ApiResponse<ContentEvent>> {
    logger.info('content', 'Updating event', { id, updates: Object.keys(updates) });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to update event: AWS not configured', { id });
      return { data: null, error: new Error('AWS not configured') };
    }

    const result = await apiClient.patch<ContentEvent>(`${ENDPOINTS.events}/${id}`, updates);
    if (result.error) {
      logger.error('content', 'Failed to update event', { id, error: result.error.message });
    } else {
      logger.info('content', 'Event updated successfully', { id });
    }
    return result;
  },

  /**
   * Delete an event
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    logger.info('content', 'Deleting event', { id });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to delete event: AWS not configured', { id });
      return { data: null, error: new Error('AWS not configured') };
    }

    const result = await apiClient.delete<void>(`${ENDPOINTS.events}/${id}`);
    if (result.error) {
      logger.error('content', 'Failed to delete event', { id, error: result.error.message });
    } else {
      logger.info('content', 'Event deleted successfully', { id });
    }
    return result;
  },

  /**
   * Reorder events
   */
  async reorder(eventOrders: { id: string; order: number }[]): Promise<ApiResponse<void>> {
    logger.debug('content', 'Reordering events', { count: eventOrders.length });

    if (!isAWSConfigured()) {
      logger.error('content', 'Failed to reorder events: AWS not configured');
      return { data: null, error: new Error('AWS not configured') };
    }

    const result = await apiClient.post<void>(`${ENDPOINTS.events}/reorder`, { orders: eventOrders });
    if (result.error) {
      logger.error('content', 'Failed to reorder events', { error: result.error.message });
    } else {
      logger.info('content', 'Events reordered successfully');
    }
    return result;
  },
};

// =============================================================================
// ELEVATE SERVICE
// =============================================================================

export const elevateService = {
  /**
   * Fetch events from the Elevate platform via backend proxy
   */
  async fetchEvents(params?: { from?: string; to?: string; status?: string }): Promise<ApiResponse<ContentEvent[]>> {
    logger.debug('content', 'Fetching Elevate events');

    if (!isAWSConfigured()) {
      logger.warn('content', 'Failed to fetch Elevate events: AWS not configured');
      return { data: [], error: null };
    }

    const queryParts: string[] = [];
    if (params?.from) queryParts.push(`from=${params.from}`);
    if (params?.to) queryParts.push(`to=${params.to}`);
    if (params?.status) queryParts.push(`status=${params.status}`);
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

    const result = await apiClient.get<ContentEvent[]>(`/elevate/events${queryString}`);
    if (result.error) {
      logger.error('content', 'Failed to fetch Elevate events', result.error);
    } else {
      logger.info('content', 'Elevate events fetched successfully', {
        count: result.data?.length ?? 0,
      });
    }
    return result;
  },

  /**
   * Check Elevate API connectivity status
   */
  async getStatus(): Promise<ApiResponse<{ connected: boolean; message: string }>> {
    if (!isAWSConfigured()) {
      return { data: { connected: false, message: 'AWS not configured' }, error: null };
    }

    return apiClient.get<{ connected: boolean; message: string }>('/elevate/status');
  },
};

// =============================================================================
// API KEYS SERVICE
// =============================================================================

export const apiKeysService = {
  /**
   * Fetch all API keys (metadata only)
   */
  async fetchAll(): Promise<ApiResponse<any[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }
    return apiClient.get<any[]>('/api-keys');
  },

  /**
   * Create a new API key
   */
  async create(name: string): Promise<ApiResponse<any>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }
    return apiClient.post<any>('/api-keys', { name });
  },

  /**
   * Revoke an API key
   */
  async revoke(id: string): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }
    return apiClient.delete<void>(`/api-keys/${id}`);
  },
};

// =============================================================================
// EXPORT DEFAULT
// =============================================================================

const contentService = {
  seeds: seedsService,
  outputs: outputsService,
  groups: groupsService,
  notes: notesService,
  events: eventsService,
  elevate: elevateService,
  apiKeys: apiKeysService,
};

export default contentService;
